import { CardBackground, Typography, useToast } from "@hellocarbo/lasagne";
import { addMonths, formatISO, subMonths } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  data,
  Form,
  redirectDocument,
  useActionData,
  useLoaderData,
  useNavigation,
} from "react-router";
import invariant from "tiny-invariant";

import {
  EmployeesTurnover,
  ExternalProvider,
  Period,
} from "@/components/ExternalCampaign/CreateExternalCampaign";
import PageWrapper from "@/components/ui/PageWrapper";
import { TTrackingCampaign } from "@/types";

export async function loader() {
  const response = await fetch("/api/client/tracking_campaigns/oldest");
  if (response.status !== 200) {
    throw data("Failed to load the oldest tracking campaign", {
      status: response.status,
    });
  }

  const { data: oldestTrackingCampaignData } = (await response.json()) as {
    data: TTrackingCampaign;
  };

  return {
    oldestCampaignPeriodStart: oldestTrackingCampaignData.period_start,
    oldestCampaignEmployees: oldestTrackingCampaignData.employees || 0,
    oldestCampaignTurnover: oldestTrackingCampaignData.turnover || 0,
  };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const startDate = formData.get("startDate") as string;
  const externalProvider = formData.get("externalProvider") as string;
  const employees = Number(formData.get("employees")) as number;
  const turnover = Number(formData.get("turnover")) as number;
  // We only accept annual campaigns for external providers
  const default_duration = 12;

  // Calculate end date (start date + duration in months)
  const endDate = formatISO(addMonths(startDate, default_duration));

  const response = await fetch("/api/client/external_campaigns/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      period_start: startDate,
      period_end: endDate,
      employees,
      turnover,
      score: {
        external_provider: externalProvider,
      },
    }),
  });

  if (response.status !== 201) {
    // This error returns a html document and cannot be parsed as JSON
    if (response.status === 500) {
      return { error: undefined, toastError: "externalCampaign:error:generic" };
    }

    const errorData = (await response.json()) as { code: string };

    if (errorData?.code === "period_intersects_existing_tracking_campaign") {
      return {
        error: "externalCampaign:error.periodsIntersect",
      };
    }

    return { toastError: "externalCampaign:error:generic" };
  }

  const {
    data: { id },
  } = (await response.json()) as {
    data: { id: number; score: { id: number } };
  };

  // Store new tracking campaign id in local storage
  localStorage.setItem("currentTrackingCampaign", `${id}`);

  return redirectDocument(`/tracking/external_campaigns/${id}/scopes`);
}

function CreateExternalCampaign() {
  const { t } = useTranslation("externalCampaign");
  const { toast } = useToast();
  const navigation = useNavigation();
  const [error, setError] = useState<string>();

  const {
    oldestCampaignPeriodStart,
    oldestCampaignTurnover,
    oldestCampaignEmployees,
  } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();

  invariant(
    oldestCampaignPeriodStart,
    "Unable to access information from the oldest campaign."
  );

  const [showEmployeesTurnover, setShowEmployeesTurnover] = useState(false);
  const [showPeriod, setShowPeriod] = useState(false);

  const defaultStartDate = subMonths(oldestCampaignPeriodStart, 12);

  useEffect(() => {
    // Show generic error message in negative toast
    if (navigation.state === "idle" && actionData?.toastError) {
      toast({
        title: t(actionData.toastError),
        intent: "negative",
      });
    }
  }, [actionData?.toastError, navigation.state, t, toast]);

  useEffect(() => {
    // Reset error when the period view is not displayed
    if (error && !showPeriod) {
      setError(undefined);
    }

    // Set an error when the period view is displayed and the action has been submitted
    if (showPeriod && navigation.state === "loading" && actionData?.error) {
      setError(actionData.error);
    }
  }, [actionData?.error, navigation.state, showPeriod, error]);

  return (
    <PageWrapper>
      <div className="my-lg grid grid-cols-4">
        <CardBackground className="col-start-2 col-end-4">
          <Typography as="h1" step="2" weight="bold" className="mb-xs">
            {t("externalCampaign:startFootprint")}
          </Typography>
          <Form method="POST">
            <div
              className={showPeriod || showEmployeesTurnover ? "hidden" : ""}
            >
              <ExternalProvider
                defaultExternalProvider=""
                onNextButtonClick={() => {
                  setShowEmployeesTurnover(true);
                }}
              />
            </div>
            <div className={!showEmployeesTurnover ? "hidden" : ""}>
              <EmployeesTurnover
                defaultEmployees={oldestCampaignEmployees}
                defaultTurnover={oldestCampaignTurnover}
                onNextButtonClick={() => {
                  setShowPeriod(true);
                  setShowEmployeesTurnover(false);
                }}
                onBackButtonClick={() => {
                  setShowEmployeesTurnover(false);
                }}
              />
            </div>
            {showPeriod && (
              <Period
                defaultStartDate={defaultStartDate}
                errorMessage={error}
                loading={navigation.state !== "idle"}
                onBackButtonClick={() => {
                  setShowPeriod(false);
                  setShowEmployeesTurnover(true);
                }}
              />
            )}
          </Form>
        </CardBackground>
      </div>
    </PageWrapper>
  );
}

export default CreateExternalCampaign;
